import { useCallback, useEffect, useRef, useState } from 'react'

import {
  createSttWebSocketUrl,
  isCorrectedMessage,
  isFinalMessage,
  safeJsonParse
} from '../services/sttWebSocket'

const STT_BASE_URL = process.env.REACT_APP_STT_SERVICE_URL!
const WORKLET_URL = '/stt-resampler-processor.js'

export type SttStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'recording'
  | 'finalizing'
  | 'stopped'
  | 'error'

export type SttSegment = {
  segmentId: number
  rawText: string
  correctedText: string | null
  done: boolean
}

type UseSttStreamOptions = {
  language?: string
  targetLanguage?: string
  audioFile?: File
}

export type UseSttStreamReturn = {
  start: (opts: UseSttStreamOptions) => Promise<void>
  stop: () => void
  cancel: () => void
  status: SttStatus
  segments: SttSegment[]
  error: string | null
  clearSegments: () => void
  resetError: () => void
  analyserNode: AnalyserNode | null
}

export function useSttStream(): UseSttStreamReturn {
  const [status, setStatus] = useState<SttStatus>('idle')
  const [segments, setSegments] = useState<SttSegment[]>([])
  const [error, setError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const sourceNodeRef = useRef<AudioNode | null>(null)
  const bufferSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)

  const cleanupAudio = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    // 1. Stop mic tracks
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null

    // 2. Disconnect audio nodes and close context
    if (bufferSourceRef.current) {
      try {
        bufferSourceRef.current.stop()
      } catch {
        // The source may already be stopped during teardown.
      }
      bufferSourceRef.current.disconnect()
      bufferSourceRef.current = null
    }
    workletNodeRef.current?.disconnect()
    sourceNodeRef.current?.disconnect()
    analyserRef.current?.disconnect()
    workletNodeRef.current = null
    sourceNodeRef.current = null
    analyserRef.current = null
    audioCtxRef.current?.close()
    audioCtxRef.current = null
  }, [])

  const closeWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onmessage = null
      wsRef.current.onerror = null
      wsRef.current.onclose = null
      if (wsRef.current.readyState < WebSocket.CLOSING) {
        wsRef.current.close(1000)
      }
    }
    wsRef.current = null
  }, [])

  const cleanup = useCallback(() => {
    cleanupAudio()
    closeWs()
  }, [cleanupAudio, closeWs])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  const upsertSegment = useCallback(
    (segmentId: number, update: Partial<Omit<SttSegment, 'segmentId'>>) => {
      setSegments(prev => {
        const idx = prev.findIndex(s => s.segmentId === segmentId)
        if (idx === -1) {
          return [
            ...prev,
            {
              segmentId,
              rawText: '',
              correctedText: null,
              done: false,
              ...update
            }
          ]
        }
        const updated = [...prev]
        updated[idx] = { ...updated[idx], ...update }
        return updated
      })
    },
    []
  )

  const start = useCallback(
    async (opts: UseSttStreamOptions) => {
      cleanup()
      setError(null)
      setSegments([])
      setStatus('connecting')

      // 1. Acquire audio source (mic or file)
      let rawAudioBuffer: ArrayBuffer | null = null
      if (opts.audioFile) {
        try {
          rawAudioBuffer = await opts.audioFile.arrayBuffer()
        } catch (err) {
          setStatus('error')
          setError(`Failed to read audio file: ${(err as Error).message}`)
          return
        }
      } else {
        let stream: MediaStream
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              channelCount: 1,
              sampleRate: 48000,
              echoCancellation: true
            }
          })
        } catch {
          setStatus('error')
          setError(
            'Microphone permission denied. Please allow access and try again.'
          )
          return
        }
        streamRef.current = stream
      }

      // 2. Open WebSocket
      const wsUrl = createSttWebSocketUrl(STT_BASE_URL, {
        language: opts.language,
        targetLanguage: opts.targetLanguage
      })
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws
      ws.binaryType = 'arraybuffer'

      ws.onmessage = (event: MessageEvent) => {
        const parsed = safeJsonParse(event.data as string)
        if (isFinalMessage(parsed)) {
          upsertSegment(parsed.segment_id, { rawText: parsed.text })
        } else if (isCorrectedMessage(parsed)) {
          upsertSegment(parsed.segment_id, {
            correctedText: parsed.text,
            done: parsed.done
          })
        }
      }

      ws.onerror = () => {
        cleanup()
        setStatus('error')
        setError('WebSocket connection error.')
      }

      ws.onclose = (event: CloseEvent) => {
        if (event.code !== 1000) {
          setStatus('error')
          setError(`Connection closed unexpectedly (code ${event.code}).`)
        }
      }

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => {
          if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
          }
          resolve()
        }
        timeoutRef.current = setTimeout(
          () => reject(new Error('WebSocket connection timeout')),
          10000
        )
      }).catch(err => {
        cleanup()
        setStatus('error')
        setError((err as Error).message)
        throw err
      })

      setStatus('connected')

      // 3. Set up AudioWorklet
      let audioCtx: AudioContext
      try {
        audioCtx = new AudioContext()
        audioCtxRef.current = audioCtx
        await audioCtx.audioWorklet.addModule(WORKLET_URL)
      } catch (err) {
        cleanup()
        setStatus('error')
        setError(
          `Failed to initialize audio processor: ${(err as Error).message}`
        )
        return
      }

      const workletNode = new AudioWorkletNode(
        audioCtx,
        'stt-resampler-processor'
      )
      workletNodeRef.current = workletNode

      workletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(event.data.buffer)
        }
      }

      const analyserNode = audioCtx.createAnalyser()
      analyserNode.fftSize = 256
      analyserNode.smoothingTimeConstant = 0.85
      analyserRef.current = analyserNode

      let sourceNode: AudioNode
      if (rawAudioBuffer) {
        let audioBuffer: AudioBuffer
        try {
          audioBuffer = await audioCtx.decodeAudioData(rawAudioBuffer)
        } catch (err) {
          cleanup()
          setStatus('error')
          setError(`Failed to decode audio file: ${(err as Error).message}`)
          return
        }
        const bufferSource = audioCtx.createBufferSource()
        bufferSource.buffer = audioBuffer
        bufferSourceRef.current = bufferSource
        bufferSource.onended = () => {
          if (bufferSourceRef.current === null) return
          bufferSourceRef.current = null
          // Give the backend time to flush remaining transcription results
          // before closing the WebSocket and transitioning to stopped.
          if (timeoutRef.current !== null) clearTimeout(timeoutRef.current)
          timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null
            cleanup()
            setStatus('stopped')
          }, 1500)
        }
        sourceNode = bufferSource
        bufferSource.start()
      } else {
        sourceNode = audioCtx.createMediaStreamSource(streamRef.current!)
      }
      sourceNodeRef.current = sourceNode
      sourceNode.connect(analyserNode)
      analyserNode.connect(workletNode)
      // Do NOT connect workletNode to audioCtx.destination (no speaker output)

      setStatus('recording')
    },
    [cleanup, upsertSegment]
  )

  const stop = useCallback(() => {
    cleanupAudio()
    setStatus('finalizing')
    // Give the backend time to flush remaining results before closing the WS
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null
      closeWs()
      setStatus('stopped')
    }, 1500)
  }, [cleanupAudio, closeWs])

  const cancel = useCallback(() => {
    cleanup()
    setStatus('idle')
  }, [cleanup])

  const clearSegments = useCallback(() => {
    setSegments([])
  }, [])

  const resetError = useCallback(() => {
    setError(null)
    setStatus(prev => (prev === 'error' ? 'idle' : prev))
  }, [])

  return {
    start,
    stop,
    cancel,
    status,
    segments,
    error,
    clearSegments,
    resetError,
    analyserNode: analyserRef.current
  }
}
